/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   หน้าแสดงข้อมูลใบสั่งซื้อ (Purchase Order — View)

   อ่านอย่างเดียว ไม่แก้ไขข้อมูลใด ๆ ใช้ API ตัวเดียวกับหน้าแก้ไข
   (GET /ERP/purchaseorder/manage.php?code=)

   เข้ามาจากการกดเลขที่เอกสารในหน้ารายการ โดยส่ง config มาทาง
   location.state เหมือนหน้า view อื่น ๆ ของ ERP
   กด "ย้อนกลับ" ของเบราว์เซอร์แล้วกลับไปหน้ารายการเดิม
   พร้อมเงื่อนไขค้นหา/หน้าที่เปิดค้างไว้ (useAccessPageState คืนค่าให้)
============================================================ */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Button,
  Card,
  Col,
  Descriptions,
  Flex,
  Row,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import PurchaseOrderService from "../../../service/ERP/PurchaseOrder.service";
import OptionService from "../../../service/ERP/Options.service";
import { TagPurchaseOrderStatus } from "../../../components/badge-and-tag/ERP";
import { productColumnView } from "./model";
import { formatMoney } from "../../../utils/util";
import useGoBack from "../../../hooks/useGoBack";
import ActionGroup, { PrintButton, ACTION_BTN as AG_BTN } from "../../../components/action-group/ActionGroup";

const poservice = PurchaseOrderService();
const opservice = OptionService();

const gotoFrom = "/ERP/purchase-order";
const dateFormat = "DD/MM/YYYY";

const showDate = (v) => (v ? dayjs(v).format(dateFormat) : "-");
const showText = (v) =>
  v === null || v === undefined || String(v).trim() === "" ? "-" : v;

function PurchaseOrderView() {
  const navigate = useNavigate();
  const location = useLocation();

  const { config } = location.state || { config: null };

  const [header, setHeader] = useState({});
  const [detail, setDetail] = useState([]);
  const [services, setServices] = useState([]);
  const [unitOption, setUnitOption] = useState([]);

  /* กลับไปหน้าที่กดรหัสเอกสารมา ถ้าไม่มีประวัติค่อยกลับหน้ารายการ */
  const handleClose = useGoBack(gotoFrom);

  const handleEdit = () => {
    navigate(`${gotoFrom}/manage/edit`, {
      state: {
        config: {
          title: "แก้ไขใบสั่งซื้อ",
          textOk: null,
          textCancel: null,
          action: "edit",
          code: header?.pocode ?? config?.code,
        },
      },
    });
  };

  const handlePrint = () => {
    const newWindow = window.open("", "_blank");
    newWindow.location.href = `/po-print/${header?.pocode ?? config?.code}`;
  };

  useEffect(() => {
    if (!config?.code) {
      handleClose();
      return;
    }

    const initial = async () => {
      const res = await poservice
        .get(config?.code)
        .catch(() => message.error("โหลดข้อมูลใบสั่งซื้อไม่สำเร็จ"));

      if (!res) return;

      const { header: h, detail: d, services: sv } = res.data || {};
      setHeader(h || {});
      setDetail(d || []);
      setServices(sv || []);

      const [unitRes] = await Promise.all([
        opservice.optionsUnit({ p: "unit-option" }),
      ]).catch(() => [{ data: { data: [] } }]);

      setUnitOption(unitRes?.data?.data || []);
    };

    initial();
    return () => {};
  }, []);

  /* ยอดสรุปท้ายบิล — ใช้ค่าที่บันทึกไว้ในหัวเอกสาร ไม่คำนวณใหม่
     เพื่อให้ตรงกับใบที่พิมพ์ออกไปแล้วเป๊ะ ๆ */
  const totalPrice = Number(header?.total_price || 0);
  const totalDiscount = Number(header?.total_discount || 0);
  const vat = Number(header?.vat || 0);
  const afterDiscount = totalPrice - totalDiscount;
  const vatAmount = (afterDiscount * vat) / 100;
  const grandTotal = Number(header?.grand_total_price || 0);

  const masterItems = [
    { label: "รหัสใบสั่งซื้อ", children: showText(header?.pocode) },
    { label: "วันที่ใบสั่งซื้อ", children: showDate(header?.podate) },
    {
      label: "สถานะ",
      children: header?.doc_status ? (
        <TagPurchaseOrderStatus result={header?.doc_status} />
      ) : (
        "-"
      ),
    },
    { label: "รหัสผู้ขาย", children: showText(header?.supcode) },
    {
      label: "ชื่อผู้ขาย",
      children: showText(header?.supname),
      span: { xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 },
    },
    {
      label: "ที่อยู่ผู้ขาย",
      children: showText(header?.address),
      span: { xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 },
    },
    { label: "ผู้ติดต่อ", children: showText(header?.contact) },
    { label: "โทรศัพท์", children: showText(header?.tel) },
    { label: "แฟกซ์", children: showText(header?.fax) },
    { label: "วันที่นัดส่งของ", children: showDate(header?.deldate) },
    { label: "การชำระเงิน", children: showText(header?.payment) },
    { label: "อ้างอิงใบเสนอราคา", children: showText(header?.poqua) },
    {
      label: "สถานที่จัดส่ง",
      children: showText(header?.ship_to),
      span: { xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 },
    },
    {
      label: "หมายเหตุ",
      children: showText(header?.remark),
      span: { xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 },
    },
  ];

  /* แถวสรุปยอด — ไม่มีคอลัมน์ Baht แยก ใช้หน่วยต่อท้ายตัวเลขแทน */
  const SummaryRow = ({ label, value, strong = false }) => (
    <Flex
      justify="end"
      align="center"
      gap={16}
      style={
        strong
          ? { borderTop: "1px solid #f0f0f0", paddingTop: 8, marginTop: 4 }
          : undefined
      }
    >
      <Typography.Text
        strong={strong}
        style={{
          minWidth: 160,
          textAlign: "right",
          fontSize: strong ? "1rem" : undefined,
        }}
      >
        {label}
      </Typography.Text>
      <Typography.Text
        type={strong ? "danger" : undefined}
        strong={strong}
        style={{
          minWidth: 170,
          textAlign: "right",
          fontSize: strong ? "1.05rem" : undefined,
        }}
      >
        {value} Baht
      </Typography.Text>
    </Flex>
  );

  const ButtonsLeft = (
    <Space align="center">
      <Button
        style={{ width: 120 }}
        icon={<ArrowLeftOutlined />}
        onClick={handleClose}
      >
        กลับ
      </Button>
    </Space>
  );

  const ButtonsRight = (
    <Space
      align="center"
      className="flex justify-start lg:justify-end md:justify-end"
    >
      <Flex gap={6} wrap="wrap" align="center">
        <ActionGroup type="print">
          <PrintButton onClick={handlePrint}>พิมพ์ใบสั่งซื้อ</PrintButton>
        </ActionGroup>
        {/* แก้ไขได้เฉพาะใบที่ยังไม่ได้รับของ ตามเงื่อนไขเดียวกับหน้าแก้ไข */}
        <ActionGroup type="action">
          {header?.doc_status === "ยังไม่ได้รับของ" && (
            <Button
              className="bn-center justify-center"
              style={{ ...AG_BTN, borderColor: "#52c41a", color: "#fff", background: "#52c41a" }}
              icon={<EditOutlined />}
              onClick={handleEdit}
            >
              แก้ไขใบสั่งซื้อ
            </Button>
          )}
        </ActionGroup>
      </Flex>
    </Space>
  );

  return (
    <div className="purchaseorder-view">
      <Space
        direction="vertical"
        size="middle"
        style={{ display: "flex", position: "relative" }}
      >
        <Row
          gutter={[{ xs: 32, sm: 32, md: 32, lg: 12, xl: 12 }, 8]}
          className="m-0"
        >
          <Col xs={24} sm={24} md={12} lg={12} xl={12} className="px-0">
            {ButtonsLeft}
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} className="px-0">
            {ButtonsRight}
          </Col>
        </Row>

        <Descriptions
          bordered
          size="small"
          column={{ xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 }}
          items={masterItems}
        />

        <Card
          title={
            <Typography.Title className="m-0 !text-zinc-800" level={4}>
              รายการสินค้าใบสั่งซื้อ
            </Typography.Title>
          }
        >
          <Table
            bordered
            size="small"
            rowKey={(r) => r?.code ?? r?.stcode}
            dataSource={detail}
            columns={productColumnView(unitOption)}
            pagination={false}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: <span>ไม่มีรายการสินค้า</span> }}
          />

          {services.length > 0 && (
            /* ค่าบริการเพิ่มของใบนี้ — คิดรวมอยู่ในยอดรวมแล้ว */
            <div className="mt-4">
              <Typography.Title level={5} className="!mb-2">
                ค่าบริการเพิ่ม
              </Typography.Title>
              <Table
                size="small"
                bordered
                rowKey={(r) => r.id || r.seq}
                dataSource={services}
                pagination={false}
                scroll={{ x: "max-content" }}
                columns={[
                  { title: "#", dataIndex: "seq", width: 60, align: "center" },
                  { title: "รหัสค่าบริการ", dataIndex: "service_code", width: 160 },
                  { title: "ชื่อค่าบริการ", dataIndex: "service_name" },
                  {
                    title: "จำนวนเงิน",
                    dataIndex: "amount",
                    width: 150,
                    align: "right",
                    render: (v) => formatMoney(Number(v || 0), 2),
                  },
                  { title: "หมายเหตุ", dataIndex: "remark", width: 220 },
                ]}
              />
            </div>
          )}

          {detail.length > 0 && (
            <Flex vertical gap={4} className="mt-3">
              <SummaryRow
                label="ยอดรวม"
                value={formatMoney(totalPrice, 2)}
              />
              <SummaryRow
                label={`ส่วนลดรวม${
                  header?.discount_percent
                    ? ` (${header.discount_percent}%)`
                    : ""
                }`}
                value={formatMoney(totalDiscount, 2)}
              />
              <SummaryRow
                label={`ภาษีมูลค่าเพิ่ม ${vat}%`}
                value={formatMoney(vatAmount, 2)}
              />
              <SummaryRow
                label="ยอดสุทธิ"
                value={formatMoney(grandTotal, 2)}
                strong
              />
            </Flex>
          )}
        </Card>

        <Row
          gutter={[{ xs: 32, sm: 32, md: 32, lg: 12, xl: 12 }, 8]}
          className="mx-0 mb-4"
        >
          <Col xs={24} sm={24} md={12} lg={12} xl={12} className="px-0">
            {ButtonsLeft}
          </Col>
        </Row>
      </Space>
    </div>
  );
}

export default PurchaseOrderView;
